import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import { Trash2, Share2, Save, Download, Plus, X, MapPin, Thermometer, Wind, Gauge } from 'lucide-react';
import { API_BASE_URL } from '@/config/api';
import {
  BarChart,
  Bar,
  RadarChart,
  PolarGrid,
  PolarAngleAxis,
  PolarRadiusAxis,
  Radar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  LineChart,
  Line,
} from 'recharts';

interface CityWeather {
  location: string;
  latitude: number;
  longitude: number;
  temperature: number;
  feels_like: number;
  humidity: number;
  wind_speed: number;
  description: string;
  pressure: number;
  weather_code?: number;
}

interface ComparisonPreset {
  id: string;
  name: string;
  cities: CityWeather[];
  createdAt: string;
}

interface WeatherCache {
  data: CityWeather;
  timestamp: number;
}

// Cache TTL: 10 Minuten (aligned mit Backend)
const WEATHER_CACHE_TTL = 10 * 60 * 1000;

// Helper: Cache Key generieren
function getCacheKey(lat: number, lon: number): string {
  return `weather_cache_${lat.toFixed(4)}_${lon.toFixed(4)}`;
}

// Helper: Cache lesen
function getFromCache(lat: number, lon: number): CityWeather | null {
  try {
    const cacheKey = getCacheKey(lat, lon);
    const cached = localStorage.getItem(cacheKey);
    if (!cached) return null;

    const { data, timestamp }: WeatherCache = JSON.parse(cached);
    const age = Date.now() - timestamp;

    if (age > WEATHER_CACHE_TTL) {
      localStorage.removeItem(cacheKey);
      return null;
    }

    return data;
  } catch (error) {
    console.error('Cache read error:', error);
    return null;
  }
}

// Helper: Cache schreiben
function saveToCache(weather: CityWeather): void {
  try {
    const cacheKey = getCacheKey(weather.latitude, weather.longitude);
    const cacheData: WeatherCache = {
      data: weather,
      timestamp: Date.now(),
    };
    localStorage.setItem(cacheKey, JSON.stringify(cacheData));
  } catch (error) {
    console.error('Cache write error:', error);
  }
}

export function CityComparison() {
  const { t } = useTranslation();
  const [comparisonCities, setComparisonCities] = useState<CityWeather[]>([]);
  const [presets, setPresets] = useState<ComparisonPreset[]>([]);
  const [locationInput, setLocationInput] = useState('');
  const [searchResults, setSearchResults] = useState<any[]>([]);
  const [isSearching, setIsSearching] = useState(false);
  const [isLoadingWeather, setIsLoadingWeather] = useState(false);
  const [presetName, setPresetName] = useState('');
  const [showSavePreset, setShowSavePreset] = useState(false);
  const [viewType, setViewType] = useState<'cards' | 'table' | 'chart'>('cards');

  // Load presets from localStorage
  useEffect(() => {
    const saved = localStorage.getItem('comparisonPresets');
    if (saved) {
      setPresets(JSON.parse(saved));
    }
  }, []);

  // Search locations
  const handleSearch = async (query: string) => {
    if (query.length < 2) {
      setSearchResults([]);
      return;
    }

    setIsSearching(true);
    try {
      const response = await fetch(
        `${API_BASE_URL}/search/locations?q=${encodeURIComponent(query)}`,
      );
      const data = await response.json();
      setSearchResults(data || []);
    } catch (error) {
      console.error('Search error:', error);
      setSearchResults([]);
    } finally {
      setIsSearching(false);
    }
  };

  // Add city to comparison (with LocalStorage cache)
  const addCityToComparison = async (city: any) => {
    if (comparisonCities.some(c => c.latitude === city.latitude && c.longitude === city.longitude)) {
      return;
    }

    if (comparisonCities.length >= 6) {
      alert(t('comparison.maxCitiesAlert'));
      return;
    }

    // 1. Check LocalStorage Cache first
    const cachedWeather = getFromCache(city.latitude, city.longitude);
    if (cachedWeather) {
      console.log('✅ Cache HIT für', city.name, '- keine API-Anfrage nötig');
      setComparisonCities([...comparisonCities, {
        ...cachedWeather,
        location: city.name || cachedWeather.location,
      }]);
      setLocationInput('');
      setSearchResults([]);
      return;
    }

    // 2. Cache MISS - API Request notwendig
    console.log('❌ Cache MISS für', city.name, '- API-Anfrage wird durchgeführt');
    setIsLoadingWeather(true);
    try {
      const response = await fetch(
        `${API_BASE_URL}/v1/weather/current?lat=${city.latitude}&lon=${city.longitude}`,
      );
      const weather = await response.json();

      const cityWeather: CityWeather = {
        location: city.name || `${city.latitude}, ${city.longitude}`,
        latitude: city.latitude,
        longitude: city.longitude,
        temperature: weather.temperature || 0,
        feels_like: weather.feels_like || 0,
        humidity: weather.humidity || 0,
        wind_speed: weather.wind_speed || 0,
        description: weather.description || '',
        pressure: weather.pressure || 0,
        weather_code: weather.weather_code,
      };

      // 3. Save to cache
      saveToCache(cityWeather);

      setComparisonCities([...comparisonCities, cityWeather]);
      setLocationInput('');
      setSearchResults([]);
    } catch (error) {
      console.error('Weather fetch error:', error);
      alert(t('comparison.loadError'));
    } finally {
      setIsLoadingWeather(false);
    }
  };

  // Remove city from comparison
  const removeCity = (index: number) => {
    setComparisonCities(comparisonCities.filter((_, i) => i !== index));
  };

  // Save comparison as preset
  const savePreset = () => {
    if (!presetName || comparisonCities.length === 0) {
      alert('Bitte geben Sie einen Namen ein und fügen Sie mindestens eine Stadt hinzu');
      return;
    }

    const newPreset: ComparisonPreset = {
      id: Date.now().toString(),
      name: presetName,
      cities: comparisonCities,
      createdAt: new Date().toISOString(),
    };

    const updatedPresets = [...presets, newPreset];
    setPresets(updatedPresets);
    localStorage.setItem('comparisonPresets', JSON.stringify(updatedPresets));
    setPresetName('');
    setShowSavePreset(false);
  };

  // Delete preset
  const deletePreset = (presetId: string) => {
    const updatedPresets = presets.filter(p => p.id !== presetId);
    setPresets(updatedPresets);
    localStorage.setItem('comparisonPresets', JSON.stringify(updatedPresets));
  };

  // Load preset (mit Cache-Update)
  const loadPreset = async (preset: ComparisonPreset) => {
    // Direkt aus Preset laden (instant)
    setComparisonCities(preset.cities);

    // Im Hintergrund Daten aktualisieren falls veraltet
    preset.cities.forEach(async (city) => {
      const cachedWeather = getFromCache(city.latitude, city.longitude);
      if (!cachedWeather) {
        // Cache abgelaufen - im Hintergrund neu laden
        try {
          const response = await fetch(
            `${API_BASE_URL}/v1/weather/current?lat=${city.latitude}&lon=${city.longitude}`,
          );
          const weather = await response.json();
          const updatedCity: CityWeather = {
            location: city.location,
            latitude: city.latitude,
            longitude: city.longitude,
            temperature: weather.temperature || 0,
            feels_like: weather.feels_like || 0,
            humidity: weather.humidity || 0,
            wind_speed: weather.wind_speed || 0,
            description: weather.description || '',
            pressure: weather.pressure || 0,
            weather_code: weather.weather_code,
          };
          saveToCache(updatedCity);
          
          // UI aktualisieren
          setComparisonCities(prev => 
            prev.map(c => 
              c.latitude === city.latitude && c.longitude === city.longitude 
                ? updatedCity 
                : c,
            ),
          );
        } catch (error) {
          console.error('Background weather update error:', error);
        }
      }
    });
  };

  // Share comparison
  const shareComparison = () => {
    const url = new URL(window.location.href);
    const citiesData = comparisonCities
      .map(c => `${c.latitude},${c.longitude}`)
      .join('|');
    url.searchParams.set('compare', citiesData);
    
    const text = `${t('nav.comparison')}: ${comparisonCities.map(c => c.location).join(', ')}`;
    
    if (navigator.share) {
      navigator.share({
        title: t('nav.comparison'),
        text,
        url: url.toString(),
      });
    } else {
      navigator.clipboard.writeText(url.toString());
      alert(t('ui.linkCopied'));
    }
  };

  // Export as CSV
  const exportCSV = () => {
    const headers = [t('comparison.city'), `${t('comparison.temperature')} (°C)`, `${t('comparison.feelsLike')} (°C)`, `${t('comparison.humidity')} (%)`, `${t('comparison.wind')} (km/h)`, `${t('comparison.pressure')} (hPa)`, t('weather.description')];
    const rows = comparisonCities.map(city => [
      city.location,
      city.temperature.toFixed(1),
      city.feels_like.toFixed(1),
      city.humidity,
      city.wind_speed.toFixed(1),
      city.pressure.toFixed(0),
      city.description,
    ]);

    const csv = [headers, ...rows].map(row => row.join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `wetter-vergleich-${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
  };

  // Prepare chart data
  const chartData = comparisonCities.map(city => ({
    city: city.location.split(',')[0], // Short name
    temperature: city.temperature,
    humidity: city.humidity,
    wind_speed: city.wind_speed,
    feels_like: city.feels_like,
    pressure: city.pressure / 10, // Scale for visibility
  }));

  // Radar chart data - normalized metrics per city
  const radarData = [
    {
      metric: 'Temperatur',
      ...Object.fromEntries(
        comparisonCities.map(city => [
          city.location.split(',')[0],
          Math.max(0, city.temperature + 20) // Offset to positive
        ])
      )
    },
    {
      metric: 'Luftfeuchtigkeit',
      ...Object.fromEntries(
        comparisonCities.map(city => [
          city.location.split(',')[0],
          city.humidity
        ])
      )
    },
    {
      metric: 'Windgeschwindigkeit',
      ...Object.fromEntries(
        comparisonCities.map(city => [
          city.location.split(',')[0],
          city.wind_speed * 2 // Scale for visibility
        ])
      )
    },
    {
      metric: 'Luftdruck',
      ...Object.fromEntries(
        comparisonCities.map(city => [
          city.location.split(',')[0],
          city.pressure / 10 // Scale down
        ])
      )
    }
  ];

  const CustomTooltip = ({ active, payload }: any) => {
    if (active && payload && payload.length) {
      return (
        <div className="bg-surface p-3 rounded-lg shadow-lg border border-border">
          <p className="font-semibold text-text-primary mb-2">{payload[0].payload.city}</p>
          {payload.map((entry: any, index: number) => (
            <div key={index} className="flex items-center gap-2 text-sm">
              <div className="w-3 h-3 rounded-full" style={{ backgroundColor: entry.color }} />
              <span className="text-text-secondary">{entry.name}:</span>
              <span className="font-semibold text-text-primary">
                {entry.name === t('comparison.pressure') ? (entry.value * 10).toFixed(0) : entry.value.toFixed(1)}
                {entry.name === t('comparison.temperature') || entry.name === t('comparison.feelsLike') ? '°C' :
                 entry.name === t('comparison.humidity') ? '%' :
                 entry.name === t('comparison.wind') ? ' km/h' : ' hPa'}
              </span>
            </div>
          ))}
        </div>
      );
    }
    return null;
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <h2 className="text-2xl font-bold page-title flex items-center gap-2">
            <MapPin className="w-7 h-7 text-blue-600" />
            {t('comparison.title')}
          </h2>
          <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
            {t('ui.comparisonDescription')}
          </p>
        </div>

        <div className="flex gap-2">
          <button
            onClick={shareComparison}
            disabled={comparisonCities.length === 0}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
          >
            <Share2 className="w-4 h-4" />
            {t('comparison.share')}
          </button>
          <button
            onClick={exportCSV}
            disabled={comparisonCities.length === 0}
            className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
          >
            <Download className="w-4 h-4" />
            {t('comparison.csvExport')}
          </button>
        </div>
      </div>

      {/* Search Input */}
      <div className="space-y-3">
        <div className="flex gap-2">
          <div className="flex-1 relative">
            <input
              type="text"
              placeholder={t('comparison.searchPlaceholder')}
              value={locationInput}
              onChange={(e) => {
                setLocationInput(e.target.value);
                handleSearch(e.target.value);
              }}
              className="w-full px-4 py-3 border border-border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-surface text-text-primary placeholder:text-text-secondary placeholder:opacity-60"
            />
            {isSearching && (
              <div className="absolute right-3 top-3">
                <div className="animate-spin h-6 w-6 border-2 border-blue-500 border-t-transparent rounded-full" />
              </div>
            )}
            {isLoadingWeather && (
              <div className="absolute right-3 top-3">
                <div className="animate-spin h-6 w-6 border-2 border-green-500 border-t-transparent rounded-full" />
              </div>
            )}
          </div>
          <button
            onClick={() => setShowSavePreset(!showSavePreset)}
            disabled={comparisonCities.length === 0}
            className="px-4 py-3 bg-purple-600 text-white rounded-lg hover:bg-purple-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
          >
            <Save className="w-4 h-4" />
            Preset
          </button>
        </div>

        {/* Save Preset Form */}
        <AnimatePresence>
          {showSavePreset && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              exit={{ opacity: 0, height: 0 }}
              className="flex gap-2"
            >
              <input
                type="text"
                placeholder="Preset-Name eingeben..."
                value={presetName}
                onChange={(e) => setPresetName(e.target.value)}
                className="flex-1 px-4 py-2 border border-gray-300 rounded-lg dark:bg-gray-700 dark:border-gray-600 dark:text-white"
              />
              <button
                onClick={savePreset}
                className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700"
              >
                Speichern
              </button>
              <button
                onClick={() => setShowSavePreset(false)}
                className="px-4 py-2 bg-gray-300 dark:bg-gray-600 text-gray-800 dark:text-white rounded-lg hover:bg-gray-400"
              >
                Abbrechen
              </button>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Search Results */}
        <AnimatePresence>
          {searchResults.length > 0 && (
            <motion.div
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -10 }}
              className="bg-surface border border-border rounded-lg shadow-lg max-h-64 overflow-y-auto"
            >
              {searchResults.map((result, idx) => (
                <button
                  key={idx}
                  onClick={() => addCityToComparison(result)}
                  disabled={isLoadingWeather}
                  className="autocomplete-item w-full text-left px-4 py-3 border-b border-border last:border-b-0"
                >
                  <div className="font-medium text-text-primary">
                    {result.name}
                  </div>
                  <div className="text-sm text-text-secondary">
                    {result.country} • {result.latitude.toFixed(2)}°, {result.longitude.toFixed(2)}°
                  </div>
                </button>
              ))}
            </motion.div>
          )}
        </AnimatePresence>
      </div>

      {/* Presets */}
      {presets.length > 0 && (
        <div className="space-y-2">
          <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300">{t('comparison.savedPresetsTitle')}</h3>
          <div className="flex flex-wrap gap-2">
            {presets.map((preset) => (
              <div
                key={preset.id}
                className="flex items-center gap-2 px-3 py-2 bg-purple-100 dark:bg-purple-900/30 rounded-lg"
              >
                <button
                  onClick={() => loadPreset(preset)}
                  className="text-sm font-medium text-purple-800 dark:text-purple-300 hover:underline"
                >
                  {preset.name} ({preset.cities.length})
                </button>
                <button
                  onClick={() => deletePreset(preset.id)}
                  className="text-red-600 hover:text-red-800"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* View Type Selector */}
      {comparisonCities.length > 0 && (
        <div className="flex gap-2 button-group-wrapper w-fit">
          {([
            { type: 'cards' as const, label: t('comparison.viewCards') },
            { type: 'table' as const, label: t('comparison.viewTable') },
            { type: 'chart' as const, label: t('comparison.viewChart') },
          ] as const).map(({ type, label }) => (
            <button
              key={type}
              onClick={() => setViewType(type)}
              className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
                viewType === type
                  ? 'bg-blue-600 text-white shadow-md'
                  : 'tab-button-inactive'
              }`}
            >
              {label}
            </button>
          ))}
        </div>
      )}

      {/* City Cards View */}
      {viewType === 'cards' && comparisonCities.length > 0 && (
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
          {comparisonCities.map((city, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.9 }}
              className="p-6 bg-linear-to-br from-blue-500 to-purple-600 text-white rounded-xl shadow-lg relative"
            >
              <button
                onClick={() => removeCity(index)}
                className="absolute top-3 right-3 p-2 bg-white/20 hover:bg-white/30 rounded-full transition-colors"
              >
                <Trash2 className="w-4 h-4" />
              </button>

              <div className="mb-4">
                <h3 className="text-xl font-bold">{city.location}</h3>
                <p className="text-sm opacity-90">{city.description}</p>
              </div>

              <div className="text-5xl font-bold mb-4">
                {city.temperature.toFixed(1)}°C
              </div>

              <div className="space-y-2 text-sm">
                <div className="flex items-center justify-between">
                  <span className="opacity-90">{t('comparison.feelsLike')}:</span>
                  <span className="font-semibold">{city.feels_like.toFixed(1)}°C</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="opacity-90">{t('comparison.humidity')}:</span>
                  <span className="font-semibold">{city.humidity}%</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="opacity-90">{t('comparison.wind')}:</span>
                  <span className="font-semibold">{city.wind_speed.toFixed(1)} km/h</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="opacity-90">{t('comparison.pressure')}:</span>
                  <span className="font-semibold">{city.pressure.toFixed(0)} hPa</span>
                </div>
              </div>
            </motion.div>
          ))}
        </div>
      )}

      {/* Table View */}
      {viewType === 'table' && comparisonCities.length > 0 && (
        <div className="overflow-x-auto bg-surface rounded-xl shadow-lg">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-linear-to-r from-blue-600 to-purple-600 text-white">
                <th className="p-4 text-left font-semibold">{t('comparison.city')}</th>
                <th className="p-4 text-right font-semibold">{t('comparison.temperature')}</th>
                <th className="p-4 text-right font-semibold">{t('comparison.feelsLike')}</th>
                <th className="p-4 text-right font-semibold">{t('comparison.humidity')}</th>
                <th className="p-4 text-right font-semibold">{t('comparison.wind')}</th>
                <th className="p-4 text-right font-semibold">{t('comparison.pressure')}</th>
                <th className="p-4 text-center font-semibold">{t('comparison.action')}</th>
              </tr>
            </thead>
            <tbody>
              {comparisonCities.map((city, index) => (
                <motion.tr
                  key={index}
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.05 * index }}
                  className="border-b border-border table-row-hover"
                >
                  <td className="p-4 font-medium">
                    <div className="font-semibold text-text-primary">{city.location}</div>
                    <div className="text-xs text-text-secondary">{city.description}</div>
                  </td>
                  <td className="p-4 text-right font-bold text-orange-600 dark:text-orange-400 text-lg">
                    {city.temperature.toFixed(1)}°C
                  </td>
                  <td className="p-4 text-right text-text-primary">
                    {city.feels_like.toFixed(1)}°C
                  </td>
                  <td className="p-4 text-right text-blue-600 dark:text-blue-400 font-semibold">
                    {city.humidity}%
                  </td>
                  <td className="p-4 text-right text-teal-600 dark:text-teal-400 font-semibold">
                    {city.wind_speed.toFixed(1)} km/h
                  </td>
                  <td className="p-4 text-right text-text-primary font-semibold">
                    {city.pressure.toFixed(0)} hPa
                  </td>
                  <td className="p-4 text-center">
                    <button
                      onClick={() => removeCity(index)}
                      className="p-2 text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg transition-colors"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </td>
                </motion.tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Chart View */}
      {viewType === 'chart' && comparisonCities.length > 1 && (
        <div className="space-y-6">
          {/* Temperature Comparison Bar Chart */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="p-6 bg-surface rounded-xl shadow-lg"
          >
            <h3 className="text-lg font-semibold text-text-primary mb-6 flex items-center gap-2">
              <Thermometer className="w-5 h-5 text-orange-600" />
              {t('comparison.tempComparison')}
            </h3>
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={chartData}>
                <CartesianGrid strokeDasharray="3 3" className="stroke-gray-300 dark:stroke-gray-600" opacity={0.3} />
                <XAxis dataKey="city" className="stroke-gray-500 dark:stroke-gray-400" style={{ fontSize: '12px', fill: 'currentColor' }} />
                <YAxis className="stroke-gray-500 dark:stroke-gray-400" style={{ fontSize: '12px', fill: 'currentColor' }} label={{ value: '°C', angle: -90, position: 'insideLeft', className: 'fill-gray-500 dark:fill-gray-400' }} />
                <Tooltip content={<CustomTooltip />} />
                <Legend />
                <Bar dataKey="temperature" fill="#f97316" name="Temperatur" radius={[8, 8, 0, 0]} />
                <Bar dataKey="feels_like" fill="#fb923c" name={t('comparison.feelsLike')} radius={[8, 8, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </motion.div>

          {/* Multi-Metric Line Chart */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="p-6 bg-surface rounded-xl shadow-lg"
          >
            <h3 className="text-lg font-semibold text-text-primary mb-6 flex items-center gap-2">
              <Wind className="w-5 h-5 text-teal-600" />
              {t('comparison.metricsComparison')}
            </h3>
            <ResponsiveContainer width="100%" height={300}>
              <LineChart data={chartData}>
                <CartesianGrid strokeDasharray="3 3" className="stroke-gray-300 dark:stroke-gray-600" opacity={0.3} />
                <XAxis dataKey="city" className="stroke-gray-500 dark:stroke-gray-400" style={{ fontSize: '12px', fill: 'currentColor' }} />
                <YAxis className="stroke-gray-500 dark:stroke-gray-400" style={{ fontSize: '12px', fill: 'currentColor' }} />
                <Tooltip content={<CustomTooltip />} />
                <Legend />
                <Line type="monotone" dataKey="humidity" stroke="#3b82f6" strokeWidth={2} dot={{ fill: '#3b82f6', r: 5 }} name="Luftfeuchte" />
                <Line type="monotone" dataKey="wind_speed" stroke="#14b8a6" strokeWidth={2} dot={{ fill: '#14b8a6', r: 5 }} name="Wind" />
              </LineChart>
            </ResponsiveContainer>
          </motion.div>

          {/* Radar Chart (if 3+ cities) */}
          {comparisonCities.length >= 3 && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.4 }}
              className="p-6 bg-surface rounded-xl shadow-lg"
            >
              <h3 className="text-lg font-semibold text-text-primary mb-6 flex items-center gap-2">
                <Gauge className="w-5 h-5 text-purple-600" />
                {t('comparison.radarComparison')}
              </h3>
              <ResponsiveContainer width="100%" height={400}>
                <RadarChart data={radarData}>
                  <PolarGrid 
                    stroke="currentColor" 
                    className="stroke-gray-400 dark:stroke-gray-500" 
                    strokeOpacity={0.5}
                  />
                  <PolarAngleAxis 
                    dataKey="metric" 
                    tick={{ 
                      fill: 'currentColor',
                      fontSize: 13,
                      fontWeight: 500
                    }}
                  />
                  <PolarRadiusAxis 
                    angle={90}
                    stroke="currentColor"
                    className="stroke-gray-400 dark:stroke-gray-500"
                    tick={{ 
                      fill: 'currentColor',
                      fontSize: 11
                    }}
                  />
                  {comparisonCities.map((city, index) => {
                    const colors = ['#f97316', '#3b82f6', '#14b8a6', '#a855f7', '#ec4899'];
                    const color = colors[index % colors.length];
                    return (
                      <Radar
                        key={city.location}
                        name={city.location.split(',')[0]}
                        dataKey={city.location.split(',')[0]}
                        stroke={color}
                        fill={color}
                        fillOpacity={0.25}
                        strokeWidth={2}
                      />
                    );
                  })}
                  <Legend 
                    wrapperStyle={{ 
                      color: 'currentColor',
                      paddingTop: '20px'
                    }}
                    iconType="circle"
                  />
                </RadarChart>
              </ResponsiveContainer>
            </motion.div>
          )}
        </div>
      )}

      {/* Empty State */}
      {comparisonCities.length === 0 && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          className="text-center py-16 bg-surface-elevated rounded-xl border-2 border-dashed border-border"
        >
          <Plus className="w-16 h-16 text-gray-400 mx-auto mb-4" />
          <h3 className="text-xl font-semibold text-gray-700 dark:text-gray-300 mb-2">
            {t('comparison.emptyTitle')}
          </h3>
          <p className="text-gray-500 dark:text-gray-400">
            {t('comparison.emptyText')}
          </p>
        </motion.div>
      )}
    </div>
  );
}
